import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';
import moment from 'moment';
import {
    ORDER_DELIVERY_MODES,
    inferCustomerOrderDeliveryMode,
} from '@/utils/customer-order-delivery';

const state = {
    order: {},
    order_type: ORDER_DELIVERY_MODES.SHIPPING,
    order_saving: false,
    order_saving_errors: {},

    order_loading: false,
    order_loading_errors: {},

    delivery_date: null,

    buyer_id: null,
    buyer: null,
    buyer_loading: false,
    buyer_loading_errors: {},

    deliverysite_id: null,
    deliverysite: null,
    deliverysite_loading: false,
    deliverysite_loading_errors: {},

    updating_order_status: false,

    buyers: [],
    buyers_count: 0,
    buyers_loading: false,
    buyers_loading_errors: {},

    deliverysites: [],
    deliverysites_count: 0,
    deliverysites_loading: false,
    deliverysites_loading_errors: {},
};


const mutations = {
    updateOrder(state, order) {
        state.order = JSON.parse(JSON.stringify({ order_date: order?.id ? null : moment().format("YYYY-MM-DD"), ...(order || {}) }));
        state.buyer_id = order.buyer;
        state.buyer = order.buyer_data;
        state.deliverysite = order.deliverysite_data;
        state.deliverysite_id = order.deliverysite;
        state.delivery_date = order.delivery_date;
    },
    updateOrderSaving(state, saving) {
        state.order_saving = saving;
    },
    updateOrderSavingErrors(state, errors) {
        state.order_saving_errors = errors;
    },
    updateOrderLoading(state, loading) {
        state.order_loading = loading;
    },
    updateOrderLoadingErrors(state, errors) {
        state.order_loading_errors = errors;
    },
    updateBuyer(state, buyer) {
        state.buyer = buyer;
        state.buyer_id = buyer?.id;
        state.order.buyer = buyer?.id;
        state.order.recipient = buyer?.id;
        state.deliverysite_id = null;
        state.deliverysite = null;
        state.order.deliverysite = null;
    },
    updateBuyerId(state, buyer_id) {
        state.buyer_id = buyer_id;
        state.order.buyer = buyer_id;
        state.order.recipient = buyer_id;
        state.deliverysite_id = null;
        state.deliverysite = null;
        state.order.deliverysite = null;
    },

    updateDeliverySiteId(state, deliverysite_id) {
        state.deliverysite_id = deliverysite_id;
        state.order.deliverysite = deliverysite_id;
    },
    updateDeliverySiteLoadingErrors(state, errors) {
        state.deliverysite_loading_errors = errors;
    },
    updateDeliverySiteLoading(state, loading) {
        state.deliverysite_loading = loading;
    },
    updateDeliverySite(state, deliverysite) {
        state.deliverysite = deliverysite;
        state.deliverysite_id = deliverysite?.id;
        state.order.deliverysite = deliverysite?.id;
    },

    updateBuyerLoadingErrors(state, errors) {
        state.buyer_loading_errors = errors;
    },
    updateBuyerLoading(state, loading) {
        state.buyer_loading = loading;
    },



    updateBuyers(state, buyers) {
        state.buyers = buyers;
    },
    updateBuyersCount(state, count) {
        state.buyers_count = count;
    },
    updateBuyersLoading(state, loading) {
        state.buyers_loading = loading;
    },
    updateBuyersLoadingErrors(state, errors) {
        state.buyers_loading_errors = errors;
    },

    updateDeliverySites(state, deliverysites) {
        state.deliverysites = deliverysites;
        if (
            state.order_type !== ORDER_DELIVERY_MODES.SHIPPING ||
            state.order.deliverysite
        ) {
            return;
        }
        // Several addresses: leave the field empty so the seller must pick one.
        // A single active site is unambiguous, so it can be selected for them.
        const selectable = deliverysites.filter((site) => !site.archived);
        if (selectable.length === 1) {
            state.deliverysite_id = selectable[0].id;
            state.order.deliverysite = selectable[0].id;
        }
    },
    updateDeliverySitesCount(state, count) {
        state.deliverysites_count = count;
    },
    updateDeliverySitesLoading(state, loading) {
        state.deliverysites_loading = loading;
    },
    updateDeliverySitesLoadingErrors(state, errors) {
        state.deliverysites_loading_errors = errors;
    },
    updateOrderDate(state, order_date) {
        state.order.order_date = order_date;
    },
    updateDeliveryDate(state, delivery_date) {
        state.order.delivery_date = delivery_date;
        state.delivery_date = delivery_date;
    },
    updateOrderType(state, order_type) {
        state.order_type = order_type;
        if (order_type !== ORDER_DELIVERY_MODES.SHIPPING) {
            state.deliverysite_id = null;
            state.deliverysite = null;
            state.order.deliverysite = null;
        }
    },

};

const actions = {
    setBuyer({ commit, dispatch, state }, buyer) {
        commit('updateBuyer', buyer);
        if (state.order_type === ORDER_DELIVERY_MODES.SHIPPING) {
            dispatch('fetchDeliverySites');
        }
    },

    async saveOrder({ commit, dispatch, state }, params) {
        commit('updateOrderSaving', true);
        commit('updateOrderSavingErrors', {});

        let url = '/api/customerorders/';
        let method = axios.post;

        if (state.order.id) {
            url = `/api/customerorders/${state.order.id}/`;
            method = axios.put;
        }
        try {
            const payload = { ...state.order, delivery_mode: state.order_type, update_shipment_dates: params?.updateShipmentDates === true };
            if (state.order_type !== ORDER_DELIVERY_MODES.SHIPPING) {
                payload.deliverysite = null;
            }
            const response = await method(url, payload);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateOrderSavingErrors', error.details);
            throw error;
        } finally {
            commit('updateOrderSaving', false);
            dispatch('session/fetchStats', null, { root: true });
        }

    },

    async fetchBuyers({ commit, dispatch, state }, params) {
        commit('updateBuyersLoading', true);
        commit('updateBuyersLoadingErrors', {});
        try {
            const response = await axios.get('/api/customers/', { params: { limit: 1000, ordering: "name", profile: "customer" } });
            commit('updateBuyers', response.data.results);
            commit('updateBuyersCount', response.data.count);
            return response;
        } catch (xhr_error) {
            let error = utils.handleError(xhr_error);
            commit('updateBuyersLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateBuyersLoading', false);
        }
    },

    async fetchDeliverySite({ commit, dispatch, state }, params) {
        commit('updateDeliverySiteLoading', true);
        commit('updateDeliverySiteLoadingErrors', {});
        try {
            const response = await axios.get(`/api/customers/${state.buyer_id}/deliverysites/${state.deliverysite_id}/`);
            commit('updateDeliverySite', response.data);
            dispatch('fetchDeliverySites');
            return response.data;
        } catch (xhr_error) {
            let error = utils.handleError(xhr_error);
            commit('updateDeliverySiteLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateDeliverySiteLoading', false);
        }
    },

    async fetchBuyer({ commit, dispatch, state }, params) {
        commit('updateDeliverySites', []);
        commit('updateBuyerLoading', true);
        commit('updateBuyerLoadingErrors', {});
        try {
            const response = await axios.get(`/api/entities/${state.buyer_id}/`, { params: { profile: "customer" } });
            commit('updateBuyer', response.data);
            dispatch('fetchDeliverySites');
            return response.data;
        } catch (xhr_error) {
            let error = utils.handleError(xhr_error);
            commit('updateBuyerLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateBuyerLoading', false);
        }
    },

    async fetchDeliverySites({ commit, dispatch, state }, params) {
        if (store.getters['session/current_user_permissions'].indexOf("core.view_customer") == -1) {
            return;
        }
        if (!state.buyer_id) {
            return
        }

        commit('updateDeliverySitesLoading', true);
        commit('updateDeliverySitesLoadingErrors', {});
        try {
            const response = await axios.get(`/api/customers/${state.buyer_id}/deliverysites/`, { params: { limit: 1000, ordering: "name" } });
            commit('updateDeliverySites', response.data.results);
            commit('updateDeliverySitesCount', response.data.count);
            return response;
        } catch (xhr_error) {
            let error = utils.handleError(xhr_error);
            commit('updateDeliverySitesLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateDeliverySitesLoading', false);
        }
    },

    initOrder({ commit, dispatch, state, rootGetters }, order) {
        commit('updateBuyerId', null);
        commit('updateBuyer', null);
        commit('updateDeliverySite', null);
        commit('updateDeliverySiteId', null);
        commit('updateDeliveryDate', null);

        dispatch('fetchBuyers');
        const initialOrder = { ...(order || {}) };
        if (!initialOrder.id && !initialOrder.assigned_to) {
            initialOrder.assigned_to = rootGetters['session/current_user_id'];
        }
        if (
            !initialOrder.id &&
            (initialOrder.delivery_date == null ||
                initialOrder.delivery_date === '')
        ) {
            initialOrder.delivery_date = moment().format('YYYY-MM-DD');
        }
        commit('updateOrder', initialOrder);
        commit('updateOrderType', inferCustomerOrderDeliveryMode(initialOrder));

        if (
            initialOrder.buyer &&
            inferCustomerOrderDeliveryMode(initialOrder) === ORDER_DELIVERY_MODES.SHIPPING
        ) {
            dispatch('fetchDeliverySites');
        }
    },

    setOrderType({ commit, dispatch, state }, order_type) {
        commit('updateOrderType', order_type);
        if (order_type === ORDER_DELIVERY_MODES.SHIPPING && state.buyer_id) {
            dispatch('fetchDeliverySites');
        }
    },

    setBuyerId({ commit, dispatch, state }, buyer_id) {
        commit('updateBuyerId', buyer_id);
        dispatch('fetchBuyer');
        dispatch('fetchDeliverySites');
    },

    setDeliverySiteId({ commit, dispatch, state }, deliverysite_id) {
        commit('updateDeliverySiteId', deliverysite_id);
        if (deliverysite_id) {
            dispatch('fetchDeliverySite');
        }
    },
}

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};
